import { useUnitName } from "../UnitNames";
import { useState } from "react";
import { Button, Dropdown, Input } from "@akin2unde/flowui-react";
import type { KeyValue } from "../../models/KeyValue";
import { lookupProduct, ProductUnitPicker } from "../InventoryLines/ProductUnitPicker";
import { activeProductUnits, defaultProductUnit, sellingPrice } from "../../utils/ProductUnits";
import "./LineItemEditor.css";
export interface EditableLineItem {
    ProductCode: string;
    ProductName: string;
    Sku?: string;
    UnitCode: string;
    Quantity: number;
    BaseUnitQuantity: number;
    UnitPrice: number;
    TotalPrice: number;
    DiscountAmount?: number;
}
export function ValidateLineItems(items: unknown, products: KeyValue[]): string {
    if (!Array.isArray(items) || !items.length)
        return "Add at least one product.";
    for (const line of items) {
        if (!line.ProductCode || !line.UnitCode)
            return "Select a product and unit for every line. Legacy lines must be assigned a unit before saving.";
        if (!Number.isFinite(line.Quantity) || line.Quantity <= 0)
            return "Quantities must be greater than zero.";
        if (!Number.isFinite(line.BaseUnitQuantity) || line.BaseUnitQuantity <= 0)
            return "Select a valid conversion for every line.";
        if (!Number.isFinite(line.UnitPrice) || line.UnitPrice <= 0)
            return "Unit prices must be greater than zero.";
        if(!Number.isFinite(Number(line.DiscountAmount ?? 0)) || Number(line.DiscountAmount ?? 0)<0 || Number(line.DiscountAmount ?? 0)>line.Quantity*line.UnitPrice)return "Line discounts must be between zero and the line total.";
        const product = lookupProduct(products, line.ProductCode);
        if (!product || !activeProductUnits(product).some(x => x.UnitCode === line.UnitCode && x.BaseQuantity === line.BaseUnitQuantity))
            return "A selected product unit is unavailable. Select an active unit.";
        const unit=activeProductUnits(product).find(u=>u.UnitCode===line.UnitCode)!;
        if(line.UnitPrice!==sellingPrice(unit,product))return "A selling price has changed. Select the product unit again.";
        if(product.CostPrice!=null && line.Quantity*line.UnitPrice-Number(line.DiscountAmount||0)<line.Quantity*line.BaseUnitQuantity*product.CostPrice)return "The final selling value cannot be below cost.";
    }
    return "";
}
export default function LineItemEditor<T extends EditableLineItem>({ Items, Products, DiscountAmount = 0, AmountPaid = 0, CurrencyCode = "NGN", OnChange, Error, HideSummary = false }: {
    HideSummary?: boolean;
    Items: T[];
    Products: KeyValue[];
    DiscountAmount?: number;
    AmountPaid?: number;
    CurrencyCode?: string;
    OnChange: (items: T[]) => void;
    Error?: string;
}) {
    const unitName=useUnitName();
    const [draft, setDraft] = useState({ ProductCode: "", UnitCode: "", Quantity: 1 });
    const product = lookupProduct(Products, draft.ProductCode);
    const unit = activeProductUnits(product).find(x => x.UnitCode === draft.UnitCode);
    const money = new Intl.NumberFormat("en-NG", { style: "currency", currency: CurrencyCode || "NGN" });
    const update = (index: number, patch: Partial<T>) => OnChange(Items.map((line, i) => {
        if (i !== index)
            return line;
        const next = { ...line, ...patch };
        return { ...next, TotalPrice: next.Quantity * next.UnitPrice };
    }));
    const add = () => {
        if (!product || !unit || !Number.isFinite(draft.Quantity) || draft.Quantity <= 0)
            return;
        const price = sellingPrice(unit, product);
        const index = Items.findIndex(x => x.ProductCode === product.Code && x.UnitCode === unit.UnitCode && x.UnitPrice === price && x.BaseUnitQuantity === unit.BaseQuantity);
        if (index >= 0)
            update(index, { Quantity: Items[index].Quantity + draft.Quantity } as Partial<T>);
        else
            OnChange([...Items, { ProductCode: product.Code, ProductName: product.Name, UnitCode: unit.UnitCode, Quantity: draft.Quantity, BaseUnitQuantity: unit.BaseQuantity, UnitPrice: price, TotalPrice: draft.Quantity * price } as T]);
        setDraft({ ProductCode: "", UnitCode: "", Quantity: 1 });
    };
    const totals = CalculateLineItemTotals(Items, DiscountAmount);
    return <div className="line-item-editor right-dialog-field-full">
 <div className="line-item-picker"><strong>Add products</strong><div className="line-item-picker-fields">
 <label>Product<Dropdown searchable value={draft.ProductCode} placeholder="Search products" options={Products.map(x => ({ display: String(x.Value), value: x.Key, other: x.Other }))} onChange={o => { const next = lookupProduct(Products, String(o.value)); setDraft({ ...draft, ProductCode: String(o.value), UnitCode: defaultProductUnit(next)?.UnitCode ?? "" }); }}/></label>
 <label>Unit<ProductUnitPicker product={product} value={draft.UnitCode} onChange={u => setDraft({ ...draft, UnitCode: u.UnitCode })}/></label>
 <label>Quantity<Input type="number" min={0.000001} step="any" value={draft.Quantity} onChange={e => setDraft({ ...draft, Quantity: Number(e.target.value) })}/></label>
 <Button type="button" disabled={!unit || draft.Quantity <= 0 || sellingPrice(unit, product) <= 0} onClick={add}>Add</Button></div>
 {unit && <p>{money.format(sellingPrice(unit, product))} per unit {unit.DiscountedSellingPrice != null ? "(discounted selling price)" : "(regular selling price)"} · {draft.Quantity * unit.BaseQuantity} base units</p>}
 {product && !defaultProductUnit(product) && <p role="alert">Configure an active default sale unit and prices on this product first.</p>}
 </div>
 <div className="line-item-heading"><strong>Items ({Items.length})</strong>{Items.length > 0 && <Button type="button" variant="ghost" onClick={() => OnChange([])}>Clear all</Button>}</div>
 {!Items.length && <p className="line-item-empty">No products added yet.</p>}
 {Items.map((line, index) => <div className="line-item-row" key={index}><strong>{line.ProductName}</strong>
 <Button type="button" variant="ghost" onClick={() => OnChange(Items.filter((_, i) => i !== index))}>Remove {line.ProductName}</Button>
 <div className="line-item-row-controls">
 <label>Unit<ProductUnitPicker product={lookupProduct(Products, line.ProductCode)} value={line.UnitCode} onChange={u => update(index, { UnitCode: u.UnitCode, BaseUnitQuantity: u.BaseQuantity, UnitPrice: sellingPrice(u, lookupProduct(Products,line.ProductCode)) } as Partial<T>)}/></label>
 <label>Quantity<Input aria-label={`Quantity for ${line.ProductName}`} type="number" min={0.000001} step="any" value={line.Quantity} onChange={e => update(index, { Quantity: Number(e.target.value) } as Partial<T>)}/></label>
 <label>Unit price<Input type="number" min={0.01} step={0.01} value={line.UnitPrice} readOnly/></label>
 <div className="line-item-total"><span>Line total</span><strong>{money.format(line.Quantity * line.UnitPrice)}</strong></div>
 </div><small>{line.UnitCode ? unitName(line.UnitCode) : "Select a unit"} · stock quantity: {line.Quantity * (line.BaseUnitQuantity || 0)} base units</small>
 </div>)}
 {Error && <p role="alert" className="line-item-error">{Error}</p>}
 {!HideSummary && <div className="line-item-summary"><p>Subtotal: {money.format(totals.SubTotal)}</p><p>Discount: {money.format(DiscountAmount)}</p>
 <p>Balance due: {money.format(Math.max(0, totals.TotalAmount - AmountPaid))}</p>{AmountPaid > totals.TotalAmount && <p>Change: {money.format(AmountPaid - totals.TotalAmount)}</p>}
 <strong>Total: {money.format(totals.TotalAmount)}</strong></div>}</div>;
}
export function CalculateLineItemTotals(items: EditableLineItem[], discount = 0) {
    const SubTotal = items.reduce((sum, item) => sum + Number(item.Quantity) * Number(item.UnitPrice), 0);
    return { SubTotal, TotalAmount: Math.max(0, SubTotal - Number(discount || 0) - items.reduce((s,item)=>s+Number(item.DiscountAmount||0),0)) };
}
